// Libararies
import _ from 'lodash';
import React from 'react';

// Supermove
import {DataLoader, Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useState,
  useQuery,
  useScrollView,
  useNavigationDOM,
  useDebouncedCallback,
  useTextInput,
  useEffect,
  useMemo,
} from '@supermove/hooks';
import {
  CommunicationModel,
  CommunicationKindModel,
  ProjectModel,
  UserModel,
} from '@supermove/models';

// App
import ErrorState from '@shared/design/components/EmptyState/ErrorState';
import Line from '@shared/design/components/Line';
import ErrorCatcher from '@shared/modules/Error/components/ErrorCatcher';
import TextMessageTemplateCategory from '@shared/modules/Sms/enums/TextMessageTemplateCategory';
import CommunicationInboxProjectConversationFooter from 'modules/Communication/CommunicationInbox/ProjectConversation/components/footer/CommunicationInboxProjectConversationFooter';
import {useCommunicationNewSseSubscription} from 'modules/Communication/hooks/useCommunicationNewSseSubscription';
import CommunicationWidgetTags from 'modules/Project/V2/Show/components/widgets/CommunicationsWidget/CommunicationWidgetTags';
import CommunicationsWidgetBody from 'modules/Project/V2/Show/components/widgets/CommunicationsWidget/CommunicationsWidgetBody';
import CommunicationsWidgetFooter from 'modules/Project/V2/Show/components/widgets/CommunicationsWidget/CommunicationsWidgetFooter';
import CommunicationsWidgetHeader from 'modules/Project/V2/Show/components/widgets/CommunicationsWidget/CommunicationsWidgetHeader';

const CommunicationsWidget = ({project}: {project: ProjectModel}) => {
  const conversationsScrollView = useScrollView();
  const {params} = useNavigationDOM();
  const searchInput = useTextInput();
  const [searchQuery, setSearchQuery] = useState('');
  const [conversationKind, setConversationKind] = useState<CommunicationKindModel>();
  const [activeActionType, setActiveActionType] = useState<CommunicationKindModel>('SMS');
  const [replyThreadUuid, setReplyThreadUuid] = useState<string>();
  const [footerExpanded, setFooterExpanded] = useState(false);

  const handleChangeQuery = useDebouncedCallback((query) => {
    setSearchQuery(query);
  }, 250);

  const {loading, data, refetch} = useQuery<{
    viewer: UserModel;
    user: UserModel;
    project: ProjectModel;
    projectCommunications: CommunicationModel[];
  }>(CommunicationsWidget.query, {
    fetchPolicy: 'network-only',
    variables: {
      userUuid: project.client.primaryContact.uuid,
      projectId: project.id,
      projectUuid: project.uuid,
      categories: [TextMessageTemplateCategory.CUSTOMER],
      searchQuery,
    },
  });

  const debouncedRefetch = useDebouncedCallback(refetch, 1000);
  const {latestEvent, subscribeToProject, unsubscribe} = useCommunicationNewSseSubscription();

  useEffect(() => {
    subscribeToProject(project.id);

    return () => {
      unsubscribe();
    };
  }, [project]);

  useEffect(() => {
    if (latestEvent && project.organization.features.isEnabledRealTimeMessages) {
      debouncedRefetch();
    }
  }, [latestEvent]);

  const communicationCounts = useMemo(() => {
    if (!data?.projectCommunications) {
      return {
        emailCount: 0,
        textMessageCount: 0,
        callCount: 0,
      };
    }
    return data.projectCommunications.reduce(
      (acc, communication) => {
        if (communication.email) {
          acc.emailCount += 1;
        }
        if (communication.textMessage) {
          acc.textMessageCount += 1;
        }
        if (communication.call) {
          acc.callCount += 1;
        }
        return acc;
      },
      {emailCount: 0, textMessageCount: 0, callCount: 0},
    );
  }, [data?.projectCommunications]);

  return (
    <React.Fragment>
      <DataLoader loading={loading} data={data}>
        {({loadedData}) => {
          const {textMessageTemplatesByCategory} = loadedData.project.organization;
          const filteredData = (() => {
            if (!conversationKind) {
              return loadedData.projectCommunications;
            }
            return loadedData.projectCommunications.filter((communication) => {
              if (conversationKind === 'EMAIL') {
                return !!communication.email;
              }
              if (conversationKind === 'SMS') {
                return !!communication.textMessage;
              }
              if (conversationKind === 'CALL') {
                return !!communication.call;
              }
              return false;
            });
          })();
          return (
            <React.Fragment>
              {footerExpanded ? null : (
                <React.Fragment>
                  <Space height={12} />
                  <CommunicationWidgetTags
                    activeTag={conversationKind}
                    handleChangeTag={(newActiveTag) => {
                      setConversationKind(newActiveTag);
                      if (newActiveTag) {
                        setActiveActionType(newActiveTag);
                        if (newActiveTag !== 'EMAIL') {
                          setReplyThreadUuid(undefined);
                        }
                      }
                    }}
                    communicationCounts={communicationCounts}
                  />
                  <Line />
                  <CommunicationsWidgetHeader
                    searchInput={searchInput}
                    handleChangeQuery={handleChangeQuery}
                    isLoading={loading}
                  />
                  <CommunicationsWidgetBody
                    viewer={loadedData.viewer}
                    customer={loadedData.user}
                    conversations={filteredData}
                    conversationsScrollView={conversationsScrollView}
                    handleReplyThread={(newThreadUuid) => {
                      setActiveActionType('EMAIL');
                      setReplyThreadUuid(newThreadUuid);
                    }}
                  />
                </React.Fragment>
              )}
              {project.organization.features.isEnabledInboxActions ? (
                <ErrorCatcher ErrorComponent={() => <ErrorState title={'Something went wrong.'} />}>
                  <CommunicationInboxProjectConversationFooter
                    customerUuid={loadedData.user.uuid}
                    projectUuid={project.uuid}
                    refetch={refetch}
                    selectedTextMessageTemplateUuid={params.textMessageTemplateUuid}
                    activeActionType={activeActionType}
                    updateActiveActionType={(newActionType) => {
                      setActiveActionType(newActionType);
                      if (newActionType !== 'EMAIL') {
                        setReplyThreadUuid(undefined);
                        if (footerExpanded) {
                          setFooterExpanded(false);
                        }
                      }
                    }}
                    showSingleActionType={conversationKind}
                    replyThreadUuid={replyThreadUuid}
                    setReplyThreadUuid={setReplyThreadUuid}
                    footerExpanded={footerExpanded}
                    setFooterExpanded={setFooterExpanded}
                  />
                </ErrorCatcher>
              ) : (
                <CommunicationsWidgetFooter
                  customer={loadedData.user}
                  viewer={loadedData.viewer}
                  refetch={refetch}
                  projectId={project.id}
                  textMessageTemplates={textMessageTemplatesByCategory}
                  selectedTextMessageTemplate={_.find(
                    textMessageTemplatesByCategory,
                    (template) => template.uuid === params.textMessageTemplateUuid,
                  )}
                />
              )}
            </React.Fragment>
          );
        }}
      </DataLoader>
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
CommunicationsWidget.query = gql`
  ${CommunicationsWidgetBody.fragment}
  ${CommunicationsWidgetFooter.fragment}

  query CommunicationsWidget(
    $userUuid: String!,
    $projectId: Int!,
    $projectUuid: String!,
    $categories: [String],
    $searchQuery: String,
    $conversationKinds: [String],
  ) {
    ${gql.query}
    viewer {
        id
        ...CommunicationsWidgetFooter_Viewer
        ...CommunicationsWidgetBody_Viewer
      }
      user(uuid: $userUuid) {
        id
        uuid
        ...CommunicationsWidgetFooter_User
        ...CommunicationsWidgetBody_User
      }
      project(uuid: $projectUuid) {
        id
        organization {
          id
          textMessageTemplatesByCategory(categories: $categories) {
            id
            uuid
            ...CommunicationsWidgetFooter_TextMessageTemplate
          }
        }
      }
      projectCommunications(projectId: $projectId, searchQuery: $searchQuery, conversationKinds: $conversationKinds) {
        id
        email {
          id
        }
        textMessage {
          id
        }
        call {
          id
        }
        ...CommunicationsWidgetBody_Communication
      }
  }
`;

CommunicationsWidget.fragment = gql`
  fragment CommunicationsWidget on Project {
    id
    uuid
    client {
      id
      uuid
      primaryContact {
        id
        uuid
      }
    }
    organization {
      id
      slug
      features {
        isEnabledRealTimeMessages: isEnabled(feature: "REAL_TIME_MESSAGES")
        isEnabledInboxActions: isEnabled(feature: "INBOX_ACTIONS")
      }
    }
  }
`;

export default CommunicationsWidget;
